import React, { useState, useEffect } from 'react';
import './Admin.css';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';
import { dowLong } from '../utils/dayOfWeek';

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function Payroll() {
  const { canView, canEdit } = usePermissions();

  // Tab visibility and edit permissions
  const tabs = [
    { key: 'payroll-maintenance', label: 'Payroll Maintenance', visible: canView('payroll', 'payroll-maintenance'), editable: canEdit('payroll', 'payroll-maintenance') },
    { key: 'process-payroll', label: 'Process Payroll', visible: canView('payroll', 'process-payroll'), editable: canEdit('payroll', 'process-payroll') },
    { key: 'payroll-report', label: 'Payroll Report', visible: canView('payroll', 'payroll-report'), editable: canEdit('payroll', 'payroll-report') },
  ];
  const visibleTabs = tabs.filter(t => t.visible);
  const getTabEditable = (key) => tabs.find(t => t.key === key)?.editable ?? false;

  const [activeTab, setActiveTab] = useState(() => visibleTabs.length > 0 ? visibleTabs[0].key : 'payroll-maintenance');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // State for payroll maintenance
  const [payrollDate, setPayrollDate] = useState(new Date().toISOString().split('T')[0]);
  const [payrollLockStatus, setPayrollLockStatus] = useState({ isLocked: false, sources: [] });
  const [payrollLockLoading, setPayrollLockLoading] = useState(false);

  // State for process payroll
  const [processPayrollDate, setProcessPayrollDate] = useState('');
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [weekLockStatus, setWeekLockStatus] = useState(null);
  const [weekCheckLoading, setWeekCheckLoading] = useState(false);
  const [lockingAll, setLockingAll] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);

  // State for payroll report
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState('');
  const [selectedRunDetails, setSelectedRunDetails] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [runsRes, weeksRes] = await Promise.all([
        fetch(`${API_BASE}/payroll/runs`),
        fetch(`${API_BASE}/payroll/available-weeks`)
      ]);

      if (runsRes.ok) {
        const data = await runsRes.json();
        setPayrollRuns(data);
      }
      if (weeksRes.ok) {
        const data = await weeksRes.json();
        setAvailableWeeks(data);
      }

      fetchPayrollLockStatus(payrollDate);
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==================== HELPERS ====================

  const formatAEST = (utcDateStr) => {
    if (!utcDateStr) return '';
    const date = new Date(utcDateStr);
    return date.toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const downloadAbaFile = (abaContent, weekStartDate) => {
    const blob = new Blob([abaContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll_${weekStartDate}.aba`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ==================== PAYROLL MAINTENANCE ====================

  const fetchPayrollLockStatus = async (date) => {
    try {
      const response = await fetch(`${API_BASE}/locked-days/${date}`);
      if (response.ok) {
        const data = await response.json();
        setPayrollLockStatus({ isLocked: data.isLocked, sources: data.sources || [] });
      }
    } catch (err) {
      console.error('Error fetching payroll lock status:', err);
    }
  };

  const handleUnlockDay = async () => {
    try {
      setPayrollLockLoading(true);
      const response = await fetch(`${API_BASE}/locked-days/${payrollDate}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setPayrollLockStatus({ isLocked: false, sources: [] });
        setSuccess('Day unlocked successfully (all planners)');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to unlock day');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setPayrollLockLoading(false);
    }
  };

  // ==================== PROCESS PAYROLL ====================

  const handleProcessWeekChange = async (e) => {
    const mondayStr = e.target.value;
    setProcessPayrollDate(mondayStr);
    setWeekLockStatus(null);
    setProcessResult(null);

    if (!mondayStr) return;

    // Auto-check lock status when a week is selected
    setWeekCheckLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/payroll/check-week/${mondayStr}`);
      if (res.ok) {
        const data = await res.json();
        setWeekLockStatus(data);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to check week');
      }
    } catch (err) {
      setError('Failed to check week lock status: ' + err.message);
    } finally {
      setWeekCheckLoading(false);
    }
  };

  const handleLockAllDays = async () => {
    setLockingAll(true);
    setError('');
    try {
      const res = await auditFetch(`${API_BASE}/payroll/lock-week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mondayDate: processPayrollDate })
      });
      if (res.ok) {
        const data = await res.json();
        setSuccess(`${data.lockedCount} day(s) locked successfully!`);
        setTimeout(() => setSuccess(''), 3000);
        // Re-check lock status
        const checkRes = await fetch(`${API_BASE}/payroll/check-week/${processPayrollDate}`);
        if (checkRes.ok) setWeekLockStatus(await checkRes.json());
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to lock days');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLockingAll(false);
    }
  };

  const handleProcessPayroll = async () => {
    if (!window.confirm('Are you sure you want to process payroll for this week? This action cannot be undone.')) return;
    setProcessing(true);
    setError('');
    try {
      const res = await auditFetch(`${API_BASE}/payroll/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mondayDate: processPayrollDate })
      });
      const data = await res.json();
      if (res.ok) {
        setProcessResult(data);
        setSuccess(`Payroll processed! ${data.recordCount} payments totaling $${data.totalAmount.toFixed(2)}`);
        setTimeout(() => setSuccess(''), 5000);
        // Refresh runs list and available weeks
        const [runsRes2, weeksRes2] = await Promise.all([
          fetch(`${API_BASE}/payroll/runs`),
          fetch(`${API_BASE}/payroll/available-weeks`)
        ]);
        if (runsRes2.ok) setPayrollRuns(await runsRes2.json());
        if (weeksRes2.ok) setAvailableWeeks(await weeksRes2.json());
      } else {
        if (data.missingBankDetails) {
          const names = data.missingBankDetails.map(p => `${p.name} (missing: ${p.missing.join(', ')})`).join('; ');
          setError(`Cannot process payroll. The following people are missing bank details: ${names}`);
        } else {
          setError(data.error || 'Failed to process payroll');
        }
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  // ==================== PAYROLL REPORT ====================

  const loadRunDetails = async (runId) => {
    if (!runId) {
      setSelectedRunDetails(null);
      return;
    }
    setReportLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/payroll/runs/${runId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedRunDetails(data);
      } else {
        setError('Failed to load payroll run details');
      }
    } catch (err) {
      setError('Failed to load run details: ' + err.message);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="admin">
      <div className="admin-tabs">
        {visibleTabs.map(tab => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {loading && <div className="loading">Loading...</div>}

      {/* PAYROLL MAINTENANCE TAB */}
      {activeTab === 'payroll-maintenance' && (
        <div className="admin-section">
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Unlock a previously locked day to allow changes to assignments and attendance.
            </p>

            <div className="form-group">
              <label>Select Date:</label>
              <input
                type="date"
                value={payrollDate}
                onChange={(e) => {
                  setPayrollDate(e.target.value);
                  fetchPayrollLockStatus(e.target.value);
                }}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <span style={{ marginLeft: 8, color: '#1565c0', fontWeight: 600 }}>{dowLong(payrollDate)}</span>
            </div>

            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              backgroundColor: payrollLockStatus.isLocked ? '#fff3e0' : '#e8f5e9',
              borderRadius: '4px',
              border: payrollLockStatus.isLocked ? '1px solid #ff9800' : '1px solid #4caf50'
            }}>
              <strong>Status: </strong>
              {payrollLockStatus.isLocked ? (
                <>
                  This day is LOCKED
                  {payrollLockStatus.sources.length > 0 && (
                    <span style={{ marginLeft: '8px', fontSize: '0.9em', color: '#555' }}>
                      ({payrollLockStatus.sources.map(s => {
                        const labels = { game: 'Game Planner', coach: 'Coach Planner', court: 'Court Planner' };
                        return labels[s] || s;
                      }).join(', ')})
                    </span>
                  )}
                </>
              ) : 'This day is not locked'}
            </div>

            <button
              onClick={handleUnlockDay}
              className="btn-submit"
              disabled={!payrollLockStatus.isLocked || payrollLockLoading || !getTabEditable('payroll-maintenance')}
              style={{ marginTop: '1rem', width: 'auto', padding: '8px 20px' }}
            >
              {payrollLockLoading ? 'Unlocking...' : 'Unlock Day (All Planners)'}
            </button>
          </div>
        </div>
      )}

      {/* PROCESS PAYROLL TAB */}
      {activeTab === 'process-payroll' && (
        <div className="admin-section">
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Select a Monday to process the pay run for that week (Monday to Sunday).
            </p>

            <div className="form-group">
              <label>Select Week:</label>
              <select
                value={processPayrollDate}
                onChange={handleProcessWeekChange}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', width: '100%', maxWidth: '400px' }}
              >
                <option value="">-- Select a week --</option>
                {availableWeeks.map(w => (
                  <option key={w.mondayDate} value={w.mondayDate}>
                    {formatDate(w.mondayDate)} - {formatDate(w.sundayDate)}
                  </option>
                ))}
              </select>
              {weekCheckLoading && (
                <p style={{ marginTop: '0.5rem', fontSize: '14px', color: '#666' }}>Checking week...</p>
              )}
            </div>

            {/* Week Lock Status */}
            {weekLockStatus && (
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ marginBottom: '0.5rem' }}>Day Lock Status</h4>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                  {weekLockStatus.days.map(day => {
                    const sourceLabels = { game: 'Game', coach: 'Coach', court: 'Court' };
                    const allSources = ['game', 'coach', 'court'];
                    const locked = day.lockedSources || [];
                    const missing = allSources.filter(s => !locked.includes(s));
                    return (
                      <div key={day.date} style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '4px',
                        backgroundColor: day.isLocked ? '#e8f5e9' : '#fff3e0',
                        border: day.isLocked ? '1px solid #4caf50' : '1px solid #ff9800',
                        textAlign: 'center',
                        minWidth: '110px'
                      }}>
                        <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{day.dayName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{formatDate(day.date)}</div>
                        <div style={{ fontSize: '12px', marginTop: '4px', color: day.isLocked ? '#4caf50' : '#ff9800' }}>
                          {day.isLocked ? 'All Locked' : (
                            missing.length > 0 ? `Missing: ${missing.map(s => sourceLabels[s]).join(', ')}` : 'Unlocked'
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Lock All Days button */}
                {!weekLockStatus.allLocked && (
                  <button
                    onClick={handleLockAllDays}
                    className="btn-submit"
                    disabled={lockingAll || !getTabEditable('process-payroll')}
                    style={{ width: 'auto', padding: '8px 20px', backgroundColor: '#ff9800', marginBottom: '1rem' }}
                  >
                    {lockingAll ? 'Locking...' : 'Lock All Days'}
                  </button>
                )}

                {/* Process Payroll button */}
                {weekLockStatus.allLocked && !processResult && (
                  <button
                    onClick={handleProcessPayroll}
                    className="btn-submit"
                    disabled={processing || !getTabEditable('process-payroll')}
                    style={{ width: 'auto', padding: '10px 30px' }}
                  >
                    {processing ? 'Processing...' : 'Process Payroll'}
                  </button>
                )}
              </div>
            )}

            {/* Process Result */}
            {processResult && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #4caf50',
                  borderRadius: '4px',
                  marginBottom: '1rem'
                }}>
                  <h4 style={{ marginBottom: '0.5rem', color: '#2e7d32' }}>Payroll Processed Successfully</h4>
                  <p><strong>Payments:</strong> {processResult.recordCount}</p>
                  <p><strong>Total Amount:</strong> ${processResult.totalAmount.toFixed(2)}</p>
                  <p><strong>Week:</strong> {formatDate(processResult.weekStartDate)} - {formatDate(processResult.weekEndDate)}</p>
                </div>

                {processResult.abaFileContent && (
                  <button
                    onClick={() => downloadAbaFile(processResult.abaFileContent, processResult.weekStartDate)}
                    className="btn-submit"
                    style={{ width: 'auto', padding: '10px 30px', backgroundColor: '#1976d2' }}
                  >
                    Download ABA File
                  </button>
                )}

                {processResult.skippedPersons && processResult.skippedPersons.length > 0 && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: '#fff3e0',
                    border: '1px solid #ff9800',
                    borderRadius: '4px'
                  }}>
                    <h4 style={{ marginBottom: '0.5rem', color: '#e65100' }}>Skipped Persons (Missing Bank Details)</h4>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                      {processResult.skippedPersons.map((p, i) => (
                        <li key={i}>{p.name} - ${p.amount.toFixed(2)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PAYROLL REPORT TAB */}
      {activeTab === 'payroll-report' && (
        <div className="admin-section">
          <div className="form-container">
            <p style={{ marginBottom: '20px', color: '#666' }}>
              Select a payroll run to view its details.
            </p>

            <div className="form-group">
              <label>Select Payroll Run:</label>
              <select
                value={selectedRunId}
                onChange={(e) => {
                  setSelectedRunId(e.target.value);
                  loadRunDetails(e.target.value);
                }}
                style={{ padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd', width: '100%', maxWidth: '500px' }}
              >
                <option value="">-- Select a payroll run --</option>
                {payrollRuns.map(run => (
                  <option key={run.id} value={run.id}>
                    {formatDate(run.weekStartDate)} - {formatDate(run.weekEndDate)} | Processed: {formatAEST(run.processedAt)} | ${parseFloat(run.totalAmount).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            {reportLoading && <div className="loading">Loading report...</div>}

            {selectedRunDetails && (
              <div style={{ marginTop: '1.5rem' }}>
                {/* Summary Card */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #1976d2',
                  borderRadius: '4px',
                  marginBottom: '1.5rem'
                }}>
                  <h4 style={{ marginBottom: '0.5rem' }}>Payroll Run Summary</h4>
                  <p><strong>Processed At:</strong> {formatAEST(selectedRunDetails.processedAt)} (AEST)</p>
                  <p><strong>Week:</strong> {formatDate(selectedRunDetails.weekStartDate)} - {formatDate(selectedRunDetails.weekEndDate)}</p>
                  <p><strong>Total Amount:</strong> ${parseFloat(selectedRunDetails.totalAmount).toFixed(2)}</p>
                  <p><strong>Payments:</strong> {selectedRunDetails.recordCount}</p>
                </div>

                {/* Details Table */}
                {selectedRunDetails.details && selectedRunDetails.details.length > 0 && (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRunDetails.details.map((d, i) => (
                          <tr key={i}>
                            <td>{d.personName}</td>
                            <td>{d.role}</td>
                            <td style={{ textAlign: 'right' }}>${parseFloat(d.amount).toFixed(2)}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                          <td colSpan={2}>Total</td>
                          <td style={{ textAlign: 'right' }}>${parseFloat(selectedRunDetails.totalAmount).toFixed(2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Download ABA */}
                {selectedRunDetails.abaFileContent && (
                  <button
                    onClick={() => downloadAbaFile(selectedRunDetails.abaFileContent, selectedRunDetails.weekStartDate)}
                    className="btn-submit"
                    style={{ width: 'auto', padding: '10px 30px', backgroundColor: '#1976d2', marginTop: '1rem' }}
                  >
                    Download ABA File
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
